import { Box, Tooltip, IconButton } from '@mui/material';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  row: any;
  handleAddFriend?: (row: any) => void;
  handleCustomiseDID?: (id: string) => void;
  handleIVRConnection?: (id: string) => void;
  handleEditContact?: (id: string) => void;
  handleDeleteRecord?: (id: string) => void;
  handleSmsQuickView?: (row: any) => void;
  isEdit?: boolean;
  isDelete?: boolean;
  isDisabled?: boolean;
  interactionLocked?: boolean;
  lockTooltip?: string;
  disableDelete?: boolean;
  deleteTooltip?: string;
  t: (key: string) => string;
};

// ----------------------------------------------------------------------

function hasChannelType(row: any): row is { channelType: string } {
  return 'channelType' in row && typeof row.channelType === 'string';
}

export function RowCellChannelActions({
  row,
  handleAddFriend,
  handleCustomiseDID,
  handleIVRConnection,
  handleEditContact,
  handleDeleteRecord,
  handleSmsQuickView,
  isEdit,
  isDelete,
  isDisabled,
  interactionLocked = false,
  lockTooltip = '',
  disableDelete = false,
  deleteTooltip,
  t,
}: Props) {
  const showAddFriend =
    hasChannelType(row) && ['LINE', 'MICROSOFTTEAMS'].includes(row.channelType) && handleAddFriend;

  const showVoiceActions =
    handleCustomiseDID &&
    handleIVRConnection &&
    isEdit &&
    hasChannelType(row) &&
    row.channelType === 'VOICE';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        justifyContent: 'left',
        ...(hasChannelType(row) && !['LINE', 'MICROSOFTTEAMS'].includes(row.channelType)
          ? { justifyContent: 'flex-end' }
          : {}),
      }}
    >
      {/* Add Friend / Sync Contact */}
      {showAddFriend &&
        (interactionLocked ? (
          <Tooltip title={lockTooltip || ''}>
            <span>
              <IconButton disabled sx={{ cursor: 'not-allowed' }}>
                {row.channelType === 'MICROSOFTTEAMS' ? (
                  <Iconify
                    icon="material-symbols:sync"
                    sx={{ color: 'var(--palette-action-active)' }}
                  />
                ) : (
                  <Iconify
                    icon="fluent-mdl2:chat-invite-friend"
                    sx={{ color: 'var(--palette-action-active)' }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={row.channelType === 'MICROSOFTTEAMS' ? t('SYNC_CONTACT') : t('ADD_FRIEND')}>
            <IconButton
              onClick={() => 'id' in row && handleAddFriend(row)}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: 'primary.light',
                },
              }}
            >
              {row.channelType === 'MICROSOFTTEAMS' ? (
                <Iconify
                  icon="material-symbols:sync"
                  sx={{ color: 'var(--palette-action-active)' }}
                />
              ) : (
                <Iconify
                  icon="fluent-mdl2:chat-invite-friend"
                  sx={{ color: 'var(--palette-action-active)' }}
                />
              )}
            </IconButton>
          </Tooltip>
        ))}

      {/* Voice Channel Actions */}
      {showVoiceActions &&
        (interactionLocked ? (
          <>
            <Tooltip title={lockTooltip || ''}>
              <span>
                <IconButton disabled sx={{ cursor: 'not-allowed' }}>
                  <Iconify
                    icon="majesticons-phone-dial"
                    sx={{ color: 'var(--palette-action-active)' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={lockTooltip || ''}>
              <span>
                <IconButton disabled sx={{ cursor: 'not-allowed' }}>
                  <Iconify
                    icon="solar-call-medicine-rounded-bold"
                    sx={{ color: 'var(--palette-action-active)' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title={t('IVR_CONFIGURATION')}>
              <IconButton
                onClick={() => 'id' in row && handleIVRConnection(String(row.id))}
                sx={{
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                <Iconify
                  icon="majesticons-phone-dial"
                  sx={{ color: 'var(--palette-action-active)' }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('CUSTOMIZE_DID')}>
              <IconButton
                onClick={() => 'id' in row && handleCustomiseDID(String(row.id))}
                sx={{
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                <Iconify
                  icon="solar-call-medicine-rounded-bold"
                  sx={{ color: 'var(--palette-action-active)' }}
                />
              </IconButton>
            </Tooltip>
          </>
        ))}

      {/* SMS Credits Info Quick View Button */}
      {row.channelType === 'SMS' && handleSmsQuickView && (
        <Tooltip title={t('SMS_CREDITS_INFO')}>
          <IconButton
            onClick={() => handleSmsQuickView(row)}
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.1)',
                backgroundColor: 'primary.light',
              },
            }}
          >
            <Iconify icon="solar:chat-round-money-bold" sx={{ color: 'var(--palette-action-active)' }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Edit Button */}
      {handleEditContact &&
        isEdit &&
        (interactionLocked ? (
          <Tooltip title={lockTooltip || ''}>
            <span>
              <IconButton disabled sx={{ cursor: 'not-allowed' }}>
                {'Settingname' in row ? (
                  <Iconify icon="solar-settings-bold-duotone" />
                ) : (
                  <Iconify icon="mdi:edit-outline" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : isDisabled ? (
          <IconButton disabled>
            {'Settingname' in row ? (
              <Iconify icon="solar-settings-bold-duotone" />
            ) : (
              <Iconify icon="mdi:edit-outline" />
            )}
          </IconButton>
        ) : (
          <Tooltip title={t('EDIT')}>
            <IconButton
              onClick={() => 'id' in row && handleEditContact(String(row.id))}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: 'primary.light',
                },
              }}
            >
              {'Settingname' in row ? (
                <Iconify icon="solar-settings-bold-duotone" />
              ) : (
                <Iconify icon="mdi:edit-outline" />
              )}
            </IconButton>
          </Tooltip>
        ))}

      {/* Delete Button */}
      {isDelete && (
        <Tooltip
          title={
            interactionLocked && lockTooltip
              ? lockTooltip
              : disableDelete
                ? deleteTooltip || t('DELETE')
                : t('DELETE')
          }
        >
          <span>
            <IconButton
              color="error"
              disabled={disableDelete || interactionLocked}
              onClick={() => 'id' in row && handleDeleteRecord?.(String(row.id))}
              sx={{
                transition: 'all 0.2s ease-in-out',
                ...(interactionLocked || disableDelete
                  ? { cursor: 'not-allowed' }
                  : {
                      '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: 'error.light',
                      },
                    }),
              }}
            >
              <Iconify
                icon="material-symbols-light:delete-outline"
                sx={{ color: 'var(--palette-error-main) !important' }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
